import React, { useState, useEffect, useCallback } from 'react';
import {
  Users,
  Phone,
  Mail,
  Building2,
  Globe,
  MapPin,
  ChevronRight,
  ChevronDown,
  Plus,
  Search,
  Filter,
  Upload,
  DollarSign,
  Calendar,
  Tag,
  MoreVertical,
  PhoneCall,
  ArrowRight,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  XCircle,
  Clock,
  Sparkles,
  Table,
  LayoutGrid,
  Save,
  Copy,
  Edit3,
  CopyPlus,
  Pencil,
  ArrowDown,
  Settings,
  Trash2
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../config';
import { callsAPI } from '../services/api';
import toast from 'react-hot-toast';

// Strip DB uniqueness suffix from test phone numbers (e.g. +17372728567#1234 → +17372728567)
const callablePhone = (phone) => phone ? phone.replace(/#.*$/, '') : phone;

const PIPELINE_STAGES = [
  { id: 'lead', label: 'Leads', color: 'bg-gray-100 border-gray-300', textColor: 'text-gray-700', icon: Users },
  { id: 'contacted', label: 'Contacted', color: 'bg-blue-50 border-blue-300', textColor: 'text-blue-700', icon: Phone },
  { id: 'qualified', label: 'Qualified', color: 'bg-purple-50 border-purple-300', textColor: 'text-purple-700', icon: CheckCircle },
  { id: 'proposal', label: 'Proposal', color: 'bg-yellow-50 border-yellow-300', textColor: 'text-yellow-700', icon: DollarSign },
  { id: 'negotiation', label: 'Negotiation', color: 'bg-orange-50 border-orange-300', textColor: 'text-orange-700', icon: TrendingUp },
  { id: 'won', label: 'Won', color: 'bg-green-50 border-green-300', textColor: 'text-green-700', icon: CheckCircle },
  { id: 'lost', label: 'Lost', color: 'bg-red-50 border-red-300', textColor: 'text-red-700', icon: XCircle }
];

const LEAD_SOURCES = [
  { id: 'Heights Business Chamber', label: 'Heights Business Chamber', color: 'bg-blue-100 text-blue-800' },
  { id: 'Asian Chamber', label: 'Asian Chamber', color: 'bg-green-100 text-green-800' },
  { id: 'Focus Group', label: 'Focus Group', color: 'bg-amber-100 text-amber-800' },
  { id: 'csv_import', label: 'CSV Import', color: 'bg-gray-100 text-gray-800' },
  { id: 'manual', label: 'Manual Entry', color: 'bg-purple-100 text-purple-800' },
  { id: 'other', label: 'Other', color: 'bg-gray-100 text-gray-600' }
];

// Pipeline options for outbound calls
const VOICE_PIPELINE_OPTIONS = [
  { value: 'retell', label: 'Retell AI (Recommended)', description: 'Full-service AI calling with voicemail detection' },
  { value: 'layer1', label: 'Layer 1 (OpenAI Realtime)', description: 'Low latency, OpenAI voices' },
  { value: 'layer2', label: 'Layer 2 (Claude + Cartesia)', description: 'Claude AI with Cartesia TTS' },
  { value: 'layer3', label: 'Layer 3 (Claude + ElevenLabs)', description: 'Claude AI with ElevenLabs voices' },
  { value: 'layer4', label: 'Layer 4 (Realtime + ElevenLabs)', description: 'Best transcription + ethnic voices' }
];

// Voice options organized by pipeline
const VOICE_OPTIONS_BY_PIPELINE = {
  retell: [
    { value: '11labs-Adrian', label: 'Adrian (Default Male)' },
    { value: '11labs-Dorothy', label: 'Dorothy (Female)' },
    { value: '11labs-Drew', label: 'Drew (Male)' },
    { value: '11labs-Paul', label: 'Paul (Male)' },
    { value: '11labs-Marissa', label: 'Marissa (Female)' },
    { value: '11labs-Myra', label: 'Myra (Female)' },
    { value: '11labs-Ryan', label: 'Ryan (Male)' },
    { value: '11labs-Sarah', label: 'Sarah (Female)' }
  ],
  layer1: [
    { value: 'alloy', label: 'Alloy' },
    { value: 'ash', label: 'Ash' },
    { value: 'ballad', label: 'Ballad' },
    { value: 'coral', label: 'Coral' },
    { value: 'echo', label: 'Echo' },
    { value: 'sage', label: 'Sage' },
    { value: 'shimmer', label: 'Shimmer' },
    { value: 'verse', label: 'Verse' }
  ],
  layer2: [
    { value: 'a0e99841-438c-4a64-b679-ae501e7d6091', label: 'Barbershop Man' },
    { value: '79a125e8-cd45-4c13-8a67-188112f4dd22', label: 'British Lady' },
    { value: '95856005-0332-41b0-935f-352e296aa0df', label: 'Classy British Man' },
    { value: 'ee7ea9f8-c0c1-498c-9f62-dc2627e1c6ec', label: 'Commercial Lady' },
    { value: 'c2ac25f9-ecc4-4f56-9095-651354df60c0', label: 'Commercial Man' },
    { value: '41534e16-2966-4c6b-9670-111411def906', label: 'Confident Woman' },
    { value: '5345cf08-6f37-424d-a5d9-8ae1571e1d1a', label: 'Helpful Woman' },
    { value: '638efaaa-4d0c-442e-b701-3fae16aad012', label: 'Indian Lady' }
  ],
  layer3: [
    { value: 'YjlcD3XHztjJEo2wNszv', label: 'Tyre G (African American Male)' },
    { value: '9wYX8b0wRvLUEYtGuzP5', label: 'KeKe (Black Woman)' },
    { value: 'Ogc84JIyxBeL4VxfwZTd', label: 'Elijah May' }
  ],
  layer4: [
    { value: '9wYX8b0wRvLUEYtGuzP5', label: 'KeKe (Black Woman)' },
    { value: 'MEiBY6lrwud0dREUa5FQ', label: 'Brian (African American Radio Jockey)' },
    { value: 'YjlcD3XHztjJEo2wNszv', label: 'Tyre G (African American Male)' },
    { value: 'CwhRBWXzGAHq8TQ4Fs17', label: 'Roger (Laid-Back Male)' },
    { value: 'EXAVITQu4vr4xnSDxMaL', label: 'Sarah (Mature Female)' },
    { value: 'cgSgspJ2msm6clMCkdW9', label: 'Jessica (Playful Female)' },
    { value: 'Ogc84JIyxBeL4VxfwZTd', label: 'Elijah May' }
  ]
};

// Default voice for each pipeline
const DEFAULT_VOICE_BY_PIPELINE = {
  retell: '11labs-Adrian',
  layer1: 'coral',
  layer2: 'a0e99841-438c-4a64-b679-ae501e7d6091',
  layer3: 'YjlcD3XHztjJEo2wNszv',
  layer4: '9wYX8b0wRvLUEYtGuzP5'
};

const SalesPipelinePage = () => {
  const { user } = useAuth();
  const [stages, setStages] = useState([]);
  const [contacts, setContacts] = useState({});
  const [allContacts, setAllContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedStage, setExpandedStage] = useState('lead');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedContact, setSelectedContact] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importData, setImportData] = useState('');
  const [importTags, setImportTags] = useState('Heights Business Chamber');
  const [importLoading, setImportLoading] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [viewMode, setViewMode] = useState('table'); // 'cards' or 'table'
  const [showAddLeadModal, setShowAddLeadModal] = useState(false);
  const [newLeadData, setNewLeadData] = useState({
    name: '',
    phoneNumber: '',
    company: '',
    email: '',
    leadSource: 'Focus Group',
    customLeadSource: '',
    introMessage: '',
    question1: '',
    question2: '',
    outroMessage: '',
    callContext: ''
  });
  const [addLeadLoading, setAddLeadLoading] = useState(false);
  const [selectedSource, setSelectedSource] = useState('all');
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [expandedGroups, setExpandedGroups] = useState({});
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [editLeadData, setEditLeadData] = useState({});
  const [editLeadLoading, setEditLeadLoading] = useState(false);
  const [selectedContacts, setSelectedContacts] = useState(new Set());
  const [copiedRow, setCopiedRow] = useState(null); // Row to copy from
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [callingContactId, setCallingContactId] = useState(null);

  // AI Configuration state - per group
  const [showAiConfigModal, setShowAiConfigModal] = useState(false);
  const [editingGroupAiConfig, setEditingGroupAiConfig] = useState(null); // Which group is being configured
  const [groupAiConfigs, setGroupAiConfigs] = useState(() => {
    // Load from localStorage if available
    const saved = localStorage.getItem('salesPipelineGroupAiConfigs');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Failed to parse saved AI configs:', e);
      }
    }
    return {};
  });
  const [currentGroupConfig, setCurrentGroupConfig] = useState({
    useMainAiConfig: true,
    pipeline: 'layer1',
    voiceModel: 'coral',
    callProvider: 'retell', // 'retell' (Retell AI) or 'vonage' (OpenAI Realtime)
    aiPersonality: '',
    aiTone: 'friendly',
    aiInstructions: '',
    aiContext: ''
  });

  const getDefaultAiConfig = () => ({
    useMainAiConfig: true,
    pipeline: 'layer1',
    voiceModel: 'coral',
    callProvider: 'retell',
    aiPersonality: '',
    aiTone: 'friendly',
    aiInstructions: '',
    aiContext: ''
  });

  const openGroupAiConfig = (groupName) => {
    setEditingGroupAiConfig(groupName);
    setCurrentGroupConfig(groupAiConfigs[groupName] || getDefaultAiConfig());
    setShowAiConfigModal(true);
  };

  const handleGroupPipelineChange = (newPipeline) => {
    setCurrentGroupConfig(prev => ({
      ...prev,
      pipeline: newPipeline,
      voiceModel: DEFAULT_VOICE_BY_PIPELINE[newPipeline]
    }));
  };

  const saveGroupAiConfig = () => {
    const updatedConfigs = {
      ...groupAiConfigs,
      [editingGroupAiConfig]: currentGroupConfig
    };
    setGroupAiConfigs(updatedConfigs);
    localStorage.setItem('salesPipelineGroupAiConfigs', JSON.stringify(updatedConfigs));
    toast.success(`AI Configuration saved for ${editingGroupAiConfig}`);
    setShowAiConfigModal(false);
    setEditingGroupAiConfig(null);
  };

  const getGroupAiConfig = (groupName) => {
    return groupAiConfigs[groupName] || getDefaultAiConfig();
  };

  const fetchPipelineData = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');

      // Fetch stage counts
      const stagesRes = await fetch(`${API_BASE_URL}/api/contacts/pipeline/stages`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const stagesData = await stagesRes.json();

      if (stagesData.success) {
        setStages(stagesData.stages);
      }

      // Fetch contacts for each stage
      const contactsByStage = {};
      const allContactsList = [];
      for (const stage of PIPELINE_STAGES) {
        const res = await fetch(`${API_BASE_URL}/api/contacts/pipeline/${stage.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json();
        if (data.success) {
          contactsByStage[stage.id] = data.contacts;
          allContactsList.push(...data.contacts);
        }
      }
      setContacts(contactsByStage);
      setAllContacts(allContactsList);

      // Initialize expanded groups - default to collapsed on page load
      const sources = [...new Set(allContactsList.map(c => getLeadSourceGroup(c)))];
      const initialExpanded = {};
      sources.forEach(s => { initialExpanded[s] = false; });
      setExpandedGroups(initialExpanded);
    } catch (error) {
      console.error('Error fetching pipeline data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPipelineData();
    loadPhoneNumbers();
  }, [fetchPipelineData]);

  const loadPhoneNumbers = async () => {
    try {
      const response = await callsAPI.getPhoneNumbers();
      setPhoneNumbers(response.data.data || []);
    } catch (error) {
      console.error('Failed to load phone numbers:', error);
    }
  };

  const triggerCall = async (contact) => {
    if (!contact) return;

    if (phoneNumbers.length === 0) {
      toast.error('No phone numbers available. Please configure a phone number first.');
      return;
    }

    const fromNumber = phoneNumbers[0].phoneNumber;

    // Build questions array from contact's question fields
    const questions = [];
    if (contact.question1) questions.push(contact.question1);
    if (contact.question2) questions.push(contact.question2);

    // Debug logging
    console.log('Sales Pipeline Call - Contact Data:', {
      name: contact.name,
      phone: contact.phoneNumber,
      introMessage: contact.introMessage,
      question1: contact.question1,
      question2: contact.question2,
      outroMessage: contact.outroMessage,
      callContext: contact.callContext,
      questionsArray: questions
    });

    try {
      setCallingContactId(contact.id);

      // Get AI config for this contact's group
      const contactGroup = getLeadSourceGroup(contact);
      const groupConfig = getGroupAiConfig(contactGroup);

      const payload = {
        toNumber: callablePhone(contact.phoneNumber),
        fromNumber: fromNumber,
        introMessage: contact.introMessage || `Hello ${contact.name}, this is Alfred calling on behalf of our team.`,
        questions: questions,
        closingMessage: contact.outroMessage || 'Thank you for your time and feedback. Have a great day!',
        pipeline: groupConfig.pipeline || 'layer1',
        voiceModel: groupConfig.voiceModel || 'coral',
        provider: groupConfig.callProvider === 'retell' ? 'retell' : 'vonage',
        callContext: contact.callContext || `Call with ${contact.name}`,
        contactName: contact.name,
        // Include AI Configuration from group if not using main config
        aiConfig: !groupConfig.useMainAiConfig ? {
          personality: groupConfig.aiPersonality,
          tone: groupConfig.aiTone,
          instructions: groupConfig.aiInstructions,
          context: groupConfig.aiContext,
          source: 'sales_pipeline',
          group: contactGroup
        } : null,
        metadata: {
          source: 'sales_pipeline',
          contactId: contact.id,
          leadSource: contactGroup
        }
      };

      console.log('Sales Pipeline Call - API Payload:', payload);

      const response = await callsAPI.createOutboundCall(payload);

      if (response.data.success) {
        const questionInfo = questions.length > 0
          ? ` (${questions.length} question${questions.length > 1 ? 's' : ''})`
          : ' (no questions configured)';
        toast.success(`Call initiated to ${contact.name}${questionInfo}`);
        fetchPipelineData(); // Refresh to update call count
      } else {
        toast.error('Failed to initiate call');
      }
    } catch (error) {
      console.error('Failed to trigger call:', error);
      toast.error(error.response?.data?.error || 'Failed to initiate call');
    } finally {
      setCallingContactId(null);
    }
  };

  const getLeadSourceGroup = (contact) => {
    // Check tags first
    if (contact.tags?.includes('Heights Business Chamber')) return 'Heights Business Chamber';
    if (contact.tags?.includes('Asian Chamber')) return 'Asian Chamber';
    if (contact.tags?.includes('Focus Group')) return 'Focus Group';
    // Check leadSource
    if (contact.leadSource === 'Heights Business Chamber') return 'Heights Business Chamber';
    if (contact.leadSource === 'Asian Chamber') return 'Asian Chamber';
    if (contact.leadSource === 'Focus Group') return 'Focus Group';
    if (contact.leadSource) return contact.leadSource;
    return 'Other';
  };

  const handleStageChange = async (contactId, newStage, additionalData = {}) => {
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/api/contacts/${contactId}/stage`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ stage: newStage, ...additionalData })
      });

      if (res.ok) {
        fetchPipelineData();
        setSelectedContact(null);
      }
    } catch (error) {
      console.error('Error updating stage:', error);
    }
  };

  const handleUpdateContact = async (contactId, field, value) => {
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/api/contacts/${contactId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ [field]: value })
      });

      if (res.ok) {
        // Update local state immediately
        setAllContacts(prev => prev.map(c =>
          c.id === contactId ? { ...c, [field]: value } : c
        ));
        setContacts(prev => {
          const updated = { ...prev };
          Object.keys(updated).forEach(stage => {
            updated[stage] = updated[stage].map(c =>
              c.id === contactId ? { ...c, [field]: value } : c
            );
          });
          return updated;
        });
      }
    } catch (error) {
      console.error('Error updating contact:', error);
    }
    setEditingCell(null);
  };

  const openEditModal = (contact) => {
    setEditingLead(contact);
    setEditLeadData({
      name: contact.name || '',
      phoneNumber: callablePhone(contact.phoneNumber) || '',
      company: contact.company || '',
      email: contact.email || '',
      leadSource: contact.leadSource || getLeadSourceGroup(contact),
      introMessage: contact.introMessage || '',
      question1: contact.question1 || '',
      question2: contact.question2 || '',
      outroMessage: contact.outroMessage || '',
      callContext: contact.callContext || '',
      callTime1: contact.callTime1 || '',
      callTime2: contact.callTime2 || '',
      callTime3: contact.callTime3 || '',
      address: contact.address || '',
      website: contact.website || '',
      industry: contact.industry || '',
      notes: contact.notes || ''
    });
    setShowEditModal(true);
  };

  const handleEditLead = async () => {
    if (!editLeadData.name || !editLeadData.phoneNumber) {
      toast.error('Name and Phone Number are required');
      return;
    }

    try {
      setEditLeadLoading(true);
      const token = localStorage.getItem('authToken');

      const res = await fetch(`${API_BASE_URL}/api/contacts/${editingLead.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: editLeadData.name,
          phoneNumber: editLeadData.phoneNumber.replace(/[^\d+]/g, ''),
          company: editLeadData.company || null,
          email: editLeadData.email || null,
          leadSource: editLeadData.leadSource || null,
          tags: [editLeadData.leadSource || 'Other'],
          introMessage: editLeadData.introMessage || null,
          question1: editLeadData.question1 || null,
          question2: editLeadData.question2 || null,
          outroMessage: editLeadData.outroMessage || null,
          callContext: editLeadData.callContext || null,
          callTime1: editLeadData.callTime1 || null,
          callTime2: editLeadData.callTime2 || null,
          callTime3: editLeadData.callTime3 || null,
          address: editLeadData.address || null,
          website: editLeadData.website || null,
          industry: editLeadData.industry || null,
          notes: editLeadData.notes || null
        })
      });

      const data = await res.json();

      if (res.ok && data.success) {
        toast.success(`Lead "${editLeadData.name}" updated successfully!`);
        // Update local state immediately
        const updatedContact = data.contact;
        setAllContacts(prev => prev.map(c =>
          c.id === editingLead.id ? { ...c, ...updatedContact } : c
        ));
        setContacts(prev => {
          const updated = { ...prev };
          Object.keys(updated).forEach(stage => {
            updated[stage] = updated[stage].map(c =>
              c.id === editingLead.id ? { ...c, ...updatedContact } : c
            );
          });
          return updated;
        });
        setShowEditModal(false);
        setEditingLead(null);
      } else {
        toast.error(data.error || 'Failed to update lead');
      }
    } catch (error) {
      console.error('Error updating lead:', error);
      toast.error('Failed to update lead');
    } finally {
      setEditLeadLoading(false);
    }
  };

  const parseCSV = (csvText) => {
    const lines = csvText.trim().split('\n');
    if (lines.length < 2) return [];

    const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
    const contacts = [];

    for (let i = 1; i < lines.length; i++) {
      const values = [];
      let current = '';
      let inQuotes = false;

      for (const char of lines[i]) {
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          values.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      values.push(current.trim());

      const contact = {};
      headers.forEach((header, idx) => {
        const key = header.toLowerCase().replace(/\s+/g, '');
        if (key === 'businessname' || key === 'name') contact.businessName = values[idx];
        else if (key === 'phone') contact.phone = values[idx];
        else if (key === 'address') contact.address = values[idx];
        else if (key === 'website') contact.website = values[idx];
        else if (key === 'category') contact.category = values[idx];
        else if (key === 'aiintegrationpotential') contact.aiIntegrationPotential = values[idx];
        // Call Script Fields
        else if (key === 'intro' || key === 'intromessage') contact.introMessage = values[idx];
        else if (key === 'q1' || key === 'question1') contact.question1 = values[idx];
        else if (key === 'q2' || key === 'question2') contact.question2 = values[idx];
        else if (key === 'outro' || key === 'outromessage') contact.outroMessage = values[idx];
        else if (key === 'context' || key === 'callcontext') contact.callContext = values[idx];
        else if (key === 'calltime1' || key === 'time1') contact.callTime1 = values[idx];
        else if (key === 'calltime2' || key === 'time2') contact.callTime2 = values[idx];
        else if (key === 'calltime3' || key === 'time3') contact.callTime3 = values[idx];
        // Date/Schedule Fields
        else if (key === 'date' || key === 'calldate' || key === 'scheduleddate') contact.scheduledDate = values[idx];
        else if (key === 'nextfollowup' || key === 'followup') contact.nextFollowUp = values[idx];
      });

      if (contact.phone) contacts.push(contact);
    }

    return contacts;
  };

  const handleAddLead = async () => {
    if (!newLeadData.name || !newLeadData.phoneNumber) {
      toast.error('Name and Phone Number are required');
      return;
    }

    // Determine the actual lead source (use custom if selected)
    const actualLeadSource = newLeadData.leadSource === 'custom'
      ? (newLeadData.customLeadSource.trim() || 'Custom Group')
      : newLeadData.leadSource;

    if (newLeadData.leadSource === 'custom' && !newLeadData.customLeadSource.trim()) {
      toast.error('Please enter a name for the custom group');
      return;
    }

    try {
      setAddLeadLoading(true);
      const token = localStorage.getItem('authToken');

      const res = await fetch(`${API_BASE_URL}/api/contacts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: newLeadData.name,
          phoneNumber: newLeadData.phoneNumber.replace(/[^\d+]/g, ''), // Clean phone number
          company: newLeadData.company || null,
          email: newLeadData.email || null,
          leadSource: actualLeadSource,
          tags: [actualLeadSource],
          pipelineStage: 'lead',
          introMessage: newLeadData.introMessage || null,
          question1: newLeadData.question1 || null,
          question2: newLeadData.question2 || null,
          outroMessage: newLeadData.outroMessage || null,
          callContext: newLeadData.callContext || null
        })
      });

      const data = await res.json();

      if (res.ok && data.success) {
        toast.success(`Lead "${newLeadData.name}" added successfully!`);
        setShowAddLeadModal(false);
        setNewLeadData({
          name: '',
          phoneNumber: '',
          company: '',
          email: '',
          leadSource: 'Focus Group',
          customLeadSource: '',
          introMessage: '',
          question1: '',
          question2: '',
          outroMessage: '',
          callContext: ''
        });
        fetchPipelineData();
      } else {
        toast.error(data.error || 'Failed to add lead');
      }
    } catch (error) {
      console.error('Error adding lead:', error);
      toast.error('Failed to add lead');
    } finally {
      setAddLeadLoading(false);
    }
  };

  const handleImport = async () => {
    try {
      setImportLoading(true);
      setImportResults(null);

      const parsedContacts = parseCSV(importData);

      if (parsedContacts.length === 0) {
        setImportResults({ error: 'No valid contacts found in CSV' });
        return;
      }

      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/api/contacts/import`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          contacts: parsedContacts,
          leadSource: 'csv_import',
          tags: importTags.split(',').map(t => t.trim()).filter(t => t)
        })
      });

      const data = await res.json();
      setImportResults(data.results || data);

      if (data.success) {
        fetchPipelineData();
        // Auto-close modal after 2 seconds on successful import
        setTimeout(() => {
          setShowImportModal(false);
          setImportResults(null);
          setImportData('');
        }, 2000);
      }
    } catch (error) {
      setImportResults({ error: error.message });
    } finally {
      setImportLoading(false);
    }
  };

  const getStageCount = (stageId) => {
    const stage = stages.find(s => s.stage === stageId);
    return stage?.count || 0;
  };

  const filteredContacts = (stageId) => {
    const stageContacts = contacts[stageId] || [];
    let filtered = stageContacts;

    if (selectedSource !== 'all') {
      filtered = filtered.filter(c => getLeadSourceGroup(c) === selectedSource);
    }

    if (!searchTerm) return filtered;

    const term = searchTerm.toLowerCase();
    return filtered.filter(c =>
      c.name?.toLowerCase().includes(term) ||
      c.company?.toLowerCase().includes(term) ||
      c.industry?.toLowerCase().includes(term)
    );
  };

  const getFilteredContactsForTable = () => {
    let filtered = allContacts.filter(c => c.pipelineStage === expandedStage);

    if (selectedSource !== 'all') {
      filtered = filtered.filter(c => getLeadSourceGroup(c) === selectedSource);
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(c =>
        c.name?.toLowerCase().includes(term) ||
        c.company?.toLowerCase().includes(term) ||
        c.industry?.toLowerCase().includes(term)
      );
    }

    return filtered;
  };

  const getGroupedContacts = () => {
    const filtered = getFilteredContactsForTable();
    const grouped = {};

    filtered.forEach(contact => {
      // Collect all groups this contact belongs to
      const groups = new Set();
      // Primary group from leadSource
      const primaryGroup = getLeadSourceGroup(contact);
      groups.add(primaryGroup);
      // Also check tags for additional group memberships
      if (contact.tags && Array.isArray(contact.tags)) {
        contact.tags.forEach(tag => groups.add(tag));
      }

      groups.forEach(source => {
        if (!grouped[source]) {
          grouped[source] = [];
        }
        grouped[source].push(contact);
      });
    });

    // Sort groups - prioritize known groups, then add any custom groups alphabetically
    const sortedGroups = {};
    const knownGroups = ['Heights Business Chamber', 'Asian Chamber', 'Focus Group', 'QA Testing', 'manual', 'csv_import'];

    // First add known groups in order
    knownGroups.forEach(key => {
      if (grouped[key]) {
        sortedGroups[key] = grouped[key].sort((a, b) =>
          (a.name || '').localeCompare(b.name || '')
        );
      }
    });

    // Then add any custom groups (not in known list and not "Other")
    Object.keys(grouped)
      .filter(key => !knownGroups.includes(key) && key !== 'Other')
      .sort()
      .forEach(key => {
        sortedGroups[key] = grouped[key].sort((a, b) =>
          (a.name || '').localeCompare(b.name || '')
        );
      });

    // Finally add "Other" at the end
    if (grouped['Other']) {
      sortedGroups['Other'] = grouped['Other'].sort((a, b) =>
        (a.name || '').localeCompare(b.name || '')
      );
    }

    return sortedGroups;
  };

  const toggleGroup = (groupName) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupName]: !prev[groupName]
    }));
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const startEditing = (contactId, field, currentValue) => {
    setEditingCell({ contactId, field });
    setEditValue(currentValue || '');
  };

  const getSourceColor = (source) => {
    const found = LEAD_SOURCES.find(s => s.id === source);
    return found?.color || 'bg-gray-100 text-gray-600';
  };

  // Copy script fields from one contact to the next row
  const copyToNextRow = async (sourceContact, sourceContacts) => {
    const currentIndex = sourceContacts.findIndex(c => c.id === sourceContact.id);
    if (currentIndex === -1 || currentIndex >= sourceContacts.length - 1) {
      alert('No next row to copy to');
      return;
    }

    const targetContact = sourceContacts[currentIndex + 1];
    const scriptFields = {
      introMessage: sourceContact.introMessage,
      question1: sourceContact.question1,
      question2: sourceContact.question2,
      outroMessage: sourceContact.outroMessage,
      callContext: sourceContact.callContext,
      callTime1: sourceContact.callTime1,
      callTime2: sourceContact.callTime2,
      callTime3: sourceContact.callTime3
    };

    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/api/contacts/${targetContact.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(scriptFields)
      });

      if (res.ok) {
        // Update local state
        setAllContacts(prev => prev.map(c =>
          c.id === targetContact.id ? { ...c, ...scriptFields } : c
        ));
        setContacts(prev => {
          const updated = { ...prev };
          Object.keys(updated).forEach(stage => {
            updated[stage] = updated[stage].map(c =>
              c.id === targetContact.id ? { ...c, ...scriptFields } : c
            );
          });
          return updated;
        });
      }
    } catch (error) {
      console.error('Error copying to next row:', error);
    }
  };

  // Copy script fields to all selected contacts
  const copyToSelected = async (sourceContact) => {
    if (selectedContacts.size === 0) {
      alert('No contacts selected. Use checkboxes to select contacts first.');
      return;
    }

    const scriptFields = {
      introMessage: sourceContact.introMessage,
      question1: sourceContact.question1,
      question2: sourceContact.question2,
      outroMessage: sourceContact.outroMessage,
      callContext: sourceContact.callContext,
      callTime1: sourceContact.callTime1,
      callTime2: sourceContact.callTime2,
      callTime3: sourceContact.callTime3
    };

    const token = localStorage.getItem('authToken');

    for (const contactId of selectedContacts) {
      if (contactId === sourceContact.id) continue; // Skip source

      try {
        await fetch(`${API_BASE_URL}/api/contacts/${contactId}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(scriptFields)
        });
      } catch (error) {
        console.error('Error copying to contact:', contactId, error);
      }
    }

    // Refresh data
    fetchPipelineData();
    setSelectedContacts(new Set());
  };

  // Duplicate a group to a new group name
  const handleDuplicateGroup = async (sourceGroup) => {
    const newGroupName = prompt(`Duplicate "${sourceGroup}" group.\n\nEnter a name for the new group:`);
    if (!newGroupName || !newGroupName.trim()) return;

    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/api/contacts/duplicate-group`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sourceGroup,
          newGroupName: newGroupName.trim()
        })
      });

      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(`Group duplicated: ${data.results.created} contacts added to "${newGroupName.trim()}"`);
        fetchPipelineData();
      } else {
        toast.error(data.error || 'Failed to duplicate group');
      }
    } catch (error) {
      console.error('Error duplicating group:', error);
      toast.error('Failed to duplicate group');
    }
  };

  // Duplicate an individual contact
  const handleDuplicateContact = async (contact) => {
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/api/contacts/duplicate-contact`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ contactId: contact.id })
      });

      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(`Duplicated "${contact.name || 'Contact'}"`);
        fetchPipelineData();
      } else {
        toast.error(data.error || 'Failed to duplicate contact');
      }
    } catch (error) {
      console.error('Error duplicating contact:', error);
      toast.error('Failed to duplicate contact');
    }
  };

  // Rename a group
  const handleRenameGroup = async (oldName) => {
    const newName = prompt(`Rename group "${oldName}".\n\nEnter the new name:`);
    if (!newName || !newName.trim() || newName.trim() === oldName) return;

    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/api/contacts/rename-group`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          oldName,
          newName: newName.trim()
        })
      });

      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(`Group renamed to "${newName.trim()}" (${data.updated} contacts updated)`);
        fetchPipelineData();
      } else {
        toast.error(data.error || 'Failed to rename group');
      }
    } catch (error) {
      console.error('Error renaming group:', error);
      toast.error('Failed to rename group');
    }
  };

  // Delete/remove a contact from a group (or delete entirely if only in one group)
  const handleDeleteContact = async (contact, groupName) => {
    // Check if contact is in multiple groups
    const groups = new Set();
    if (contact.leadSource) groups.add(contact.leadSource);
    if (contact.tags && Array.isArray(contact.tags)) {
      contact.tags.forEach(t => groups.add(t));
    }
    const inMultipleGroups = groups.size > 1 && groupName;

    const message = inMultipleGroups
      ? `Remove "${contact.name}" from the "${groupName}" group? The contact will remain in other groups.`
      : `Are you sure you want to delete "${contact.name}"? This cannot be undone.`;

    if (!confirm(message)) return;

    try {
      const token = localStorage.getItem('authToken');

      if (inMultipleGroups) {
        // Remove from this group only
        const res = await fetch(`${API_BASE_URL}/api/contacts/${contact.id}/remove-from-group`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ groupName })
        });

        const data = await res.json();
        if (res.ok && data.success) {
          toast.success(`Removed "${contact.name}" from "${groupName}"`);
          fetchPipelineData();
        } else {
          toast.error(data.error || 'Failed to remove contact from group');
        }
      } else {
        // Delete entirely
        const res = await fetch(`${API_BASE_URL}/api/contacts/${contact.id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (res.ok) {
          toast.success(`Deleted "${contact.name}"`);
          setAllContacts(prev => prev.filter(c => c.id !== contact.id));
          setContacts(prev => {
            const updated = { ...prev };
            Object.keys(updated).forEach(stage => {
              updated[stage] = updated[stage].filter(c => c.id !== contact.id);
            });
            return updated;
          });
          setSelectedContacts(prev => {
            const next = new Set(prev);
            next.delete(contact.id);
            return next;
          });
        } else {
          const data = await res.json();
          toast.error(data.error || 'Failed to delete contact');
        }
      }
    } catch (error) {
      console.error('Error deleting contact:', error);
      toast.error('Failed to delete contact');
    }
  };

  const toggleContactSelection = (contactId) => {
    setSelectedContacts(prev => {
      const next = new Set(prev);
      if (next.has(contactId)) {
        next.delete(contactId);
      } else {
        next.add(contactId);
      }
      return next;
    });
  };

  const selectAllInGroup = (groupContacts) => {
    setSelectedContacts(prev => {
      const next = new Set(prev);
      const allSelected = groupContacts.every(c => next.has(c.id));
      if (allSelected) {
        // Deselect all
        groupContacts.forEach(c => next.delete(c.id));
      } else {
        // Select all
        groupContacts.forEach(c => next.add(c.id));
      }
      return next;
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  const groupedContacts = getGroupedContacts();

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Sales Pipeline</h1>
          <p className="text-sm text-gray-500 mt-1">Track and manage your leads through the sales funnel</p>
        </div>
        <div className="flex items-center gap-3">
          {/* View Toggle */}
          <div className="flex items-center bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('table')}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'table' ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Table className="w-4 h-4" />
              Table
            </button>
            <button
              onClick={() => setViewMode('cards')}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'cards' ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <LayoutGrid className="w-4 h-4" />
              Cards
            </button>
          </div>

          {/* Source Filter */}
          <select
            value={selectedSource}
            onChange={(e) => setSelectedSource(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
          >
            <option value="all">All Sources</option>
            <option value="Heights Business Chamber">Heights Business Chamber</option>
            <option value="Asian Chamber">Asian Chamber</option>
            <option value="Focus Group">Focus Group</option>
            <option value="QA Testing">QA Testing</option>
            <option value="manual">Manual Entry</option>
            <option value="csv_import">CSV Import</option>
            <option value="Other">Other</option>
          </select>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search contacts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>
          <button
            onClick={() => setShowAddLeadModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            <Plus className="w-4 h-4" />
            Add Lead
          </button>
          <button
            onClick={() => setShowImportModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            <Upload className="w-4 h-4" />
            Import Leads
          </button>
        </div>
      </div>

      {/* Pipeline Summary Cards */}
      <div className="grid grid-cols-7 gap-3 mb-6">
        {PIPELINE_STAGES.map((stage) => {
          const StageIcon = stage.icon;
          const count = getStageCount(stage.id);
          return (
            <div
              key={stage.id}
              onClick={() => setExpandedStage(stage.id)}
              className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                expandedStage === stage.id
                  ? `${stage.color} border-current ring-2 ring-offset-2 ring-indigo-500`
                  : 'bg-white border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <StageIcon className={`w-5 h-5 ${stage.textColor}`} />
                <span className={`text-2xl font-bold ${stage.textColor}`}>{count}</span>
              </div>
              <p className={`text-sm font-medium ${stage.textColor}`}>{stage.label}</p>
            </div>
          );
        })}
      </div>

      {/* Table View */}
      {viewMode === 'table' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                {PIPELINE_STAGES.find(s => s.id === expandedStage)?.label} - Table View
              </h2>
              <span className="text-sm text-gray-500">
                {Object.values(groupedContacts).flat().length} contacts
              </span>
            </div>
          </div>

          {Object.keys(groupedContacts).length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No contacts in this stage</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              {Object.entries(groupedContacts).map(([source, sourceContacts]) => (
                <div key={source} className="border-b border-gray-200 last:border-b-0">
                  {/* Group Header */}
                  <div
                    className={`flex items-center justify-between px-4 py-3 hover:bg-gray-50 ${getSourceColor(source)}`}
                  >
                    <div
                      className="flex items-center gap-3 cursor-pointer flex-1"
                      onClick={() => toggleGroup(source)}
                    >
                      {expandedGroups[source] ? (
                        <ChevronDown className="w-5 h-5" />
                      ) : (
                        <ChevronRight className="w-5 h-5" />
                      )}
                      <span className="font-semibold">{source}</span>
                      <span className="text-sm opacity-75">({sourceContacts.length} contacts)</span>
                      {!getGroupAiConfig(source).useMainAiConfig && (
                        <span className="px-2 py-0.5 text-xs bg-indigo-100 text-indigo-700 rounded-full">
                          Custom AI
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRenameGroup(source);
                        }}
                        className="flex items-center gap-1 px-2 py-1 text-xs text-gray-600 hover:text-yellow-600 hover:bg-white rounded transition-colors"
                        title={`Rename "${source}" group`}
                      >
                        <Pencil className="w-3.5 h-3.5" />
                        <span>Rename</span>
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDuplicateGroup(source);
                        }}
                        className="flex items-center gap-1 px-2 py-1 text-xs text-gray-600 hover:text-green-600 hover:bg-white rounded transition-colors"
                        title={`Duplicate "${source}" group`}
                      >
                        <CopyPlus className="w-3.5 h-3.5" />
                        <span>Duplicate</span>
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openGroupAiConfig(source);
                        }}
                        className="flex items-center gap-1 px-2 py-1 text-xs text-gray-600 hover:text-indigo-600 hover:bg-white rounded transition-colors"
                        title={`Configure AI for ${source}`}
                      >
                        <Settings className="w-3.5 h-3.5" />
                        <span>AI Config</span>
                      </button>
                    </div>
                  </div>

                  {/* Group Table */}
                  {expandedGroups[source] && (
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-10">
                            <input
                              type="checkbox"
                              checked={sourceContacts.every(c => selectedContacts.has(c.id))}
                              onChange={() => selectAllInGroup(sourceContacts)}
                              className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                              title="Select all in group"
                            />
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-44">
                            Business Name
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-28">
                            Phone
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-40">
                            Intro Message
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-40">
                            Q1 / Q2
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                            Outro
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-28">
                            Call Times
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                            Stage
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {sourceContacts.map((contact, contactIndex) => (
                          <tr key={contact.id} className={`hover:bg-gray-50 ${selectedContacts.has(contact.id) ? 'bg-indigo-50' : ''}`}>
                            {/* Checkbox */}
                            <td className="px-2 py-3 text-center">
                              <input
                                type="checkbox"
                                checked={selectedContacts.has(contact.id)}
                                onChange={() => toggleContactSelection(contact.id)}
                                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                              />
                            </td>
                            {/* Business Name - click to open full edit modal */}
                            <td className="px-4 py-3">
                              <div
                                className="flex items-center gap-2 group cursor-pointer"
                                onClick={() => openEditModal(contact)}
                              >
                                <span className="font-medium text-blue-700 hover:text-blue-900 text-sm underline decoration-dotted underline-offset-2">{contact.name}</span>
                                <Edit3 className="w-3 h-3 text-gray-400 opacity-0 group-hover:opacity-100" />
                              </div>
                            </td>

                            {/* Phone */}
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-1">
                                <span className="text-xs text-gray-600 font-mono">{callablePhone(contact.phoneNumber)}</span>
                                <button
                                  onClick={() => copyToClipboard(callablePhone(contact.phoneNumber))}
                                  className="text-gray-400 hover:text-gray-600"
                                  title="Copy"
                                >
                                  <Copy className="w-3 h-3" />
                                </button>
                              </div>
                            </td>

                            {/* Intro Message */}
                            <td className="px-4 py-3">
                              {editingCell?.contactId === contact.id && editingCell?.field === 'introMessage' ? (
                                <textarea
                                  value={editValue}
                                  onChange={(e) => setEditValue(e.target.value)}
                                  onBlur={() => handleUpdateContact(contact.id, 'introMessage', editValue)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                      e.preventDefault();
                                      handleUpdateContact(contact.id, 'introMessage', editValue);
                                    }
                                    if (e.key === 'Escape') setEditingCell(null);
                                  }}
                                  autoFocus
                                  rows={2}
                                  className="w-full px-2 py-1 border border-indigo-500 rounded focus:outline-none text-xs"
                                />
                              ) : (
                                <div
                                  className="flex items-start gap-1 group cursor-pointer"
                                  onClick={() => startEditing(contact.id, 'introMessage', contact.introMessage)}
                                >
                                  <span className="text-xs text-gray-600 line-clamp-2">
                                    {contact.introMessage || <span className="text-gray-400 italic">+ Add intro...</span>}
                                  </span>
                                  <Edit3 className="w-3 h-3 text-gray-400 opacity-0 group-hover:opacity-100 flex-shrink-0" />
                                </div>
                              )}
                            </td>

                            {/* Q1 / Q2 */}
                            <td className="px-4 py-3">
                              <div className="space-y-1">
                                {/* Q1 */}
                                {editingCell?.contactId === contact.id && editingCell?.field === 'question1' ? (
                                  <textarea
                                    value={editValue}
                                    onChange={(e) => setEditValue(e.target.value)}
                                    onBlur={() => handleUpdateContact(contact.id, 'question1', editValue)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        handleUpdateContact(contact.id, 'question1', editValue);
                                      }
                                      if (e.key === 'Escape') setEditingCell(null);
                                    }}
                                    autoFocus
                                    rows={1}
                                    className="w-full px-2 py-1 border border-indigo-500 rounded focus:outline-none text-xs"
                                  />
                                ) : (
                                  <div
                                    className="flex items-start gap-1 group cursor-pointer"
                                    onClick={() => startEditing(contact.id, 'question1', contact.question1)}
                                  >
                                    <span className="text-xs text-blue-600 line-clamp-1">
                                      Q1: {contact.question1 || <span className="text-gray-400 italic">+ Add</span>}
                                    </span>
                                    <Edit3 className="w-2 h-2 text-gray-400 opacity-0 group-hover:opacity-100 flex-shrink-0" />
                                  </div>
                                )}
                                {/* Q2 */}
                                {editingCell?.contactId === contact.id && editingCell?.field === 'question2' ? (
                                  <textarea
                                    value={editValue}
                                    onChange={(e) => setEditValue(e.target.value)}
                                    onBlur={() => handleUpdateContact(contact.id, 'question2', editValue)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        handleUpdateContact(contact.id, 'question2', editValue);
                                      }
                                      if (e.key === 'Escape') setEditingCell(null);
                                    }}
                                    autoFocus
                                    rows={1}
                                    className="w-full px-2 py-1 border border-indigo-500 rounded focus:outline-none text-xs"
                                  />
                                ) : (
                                  <div
                                    className="flex items-start gap-1 group cursor-pointer"
                                    onClick={() => startEditing(contact.id, 'question2', contact.question2)}
                                  >
                                    <span className="text-xs text-blue-600 line-clamp-1">
                                      Q2: {contact.question2 || <span className="text-gray-400 italic">+ Add</span>}
                                    </span>
                                    <Edit3 className="w-2 h-2 text-gray-400 opacity-0 group-hover:opacity-100 flex-shrink-0" />
                                  </div>
                                )}
                              </div>
                            </td>

                            {/* Outro */}
                            <td className="px-4 py-3">
                              {editingCell?.contactId === contact.id && editingCell?.field === 'outroMessage' ? (
                                <textarea
                                  value={editValue}
                                  onChange={(e) => setEditValue(e.target.value)}
                                  onBlur={() => handleUpdateContact(contact.id, 'outroMessage', editValue)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                      e.preventDefault();
                                      handleUpdateContact(contact.id, 'outroMessage', editValue);
                                    }
                                    if (e.key === 'Escape') setEditingCell(null);
                                  }}
                                  autoFocus
                                  rows={2}
                                  className="w-full px-2 py-1 border border-indigo-500 rounded focus:outline-none text-xs"
                                />
                              ) : (
                                <div
                                  className="flex items-start gap-1 group cursor-pointer"
                                  onClick={() => startEditing(contact.id, 'outroMessage', contact.outroMessage)}
                                >
                                  <span className="text-xs text-gray-600 line-clamp-2">
                                    {contact.outroMessage || <span className="text-gray-400 italic">+ Add...</span>}
                                  </span>
                                  <Edit3 className="w-3 h-3 text-gray-400 opacity-0 group-hover:opacity-100 flex-shrink-0" />
                                </div>
                              )}
                            </td>

                            {/* Call Times */}
                            <td className="px-4 py-3">
                              <div className="space-y-0.5">
                                {['callTime1', 'callTime2', 'callTime3'].map((field, idx) => (
                                  editingCell?.contactId === contact.id && editingCell?.field === field ? (
                                    <input
                                      key={field}
                                      type="text"
                                      value={editValue}
                                      onChange={(e) => setEditValue(e.target.value)}
                                      onBlur={() => handleUpdateContact(contact.id, field, editValue)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') handleUpdateContact(contact.id, field, editValue);
                                        if (e.key === 'Escape') setEditingCell(null);
                                      }}
                                      autoFocus
                                      placeholder="HH:MM"
                                      className="w-full px-1 py-0.5 border border-indigo-500 rounded focus:outline-none text-xs"
                                    />
                                  ) : (
                                    <div
                                      key={field}
                                      className="flex items-center gap-1 group cursor-pointer"
                                      onClick={() => startEditing(contact.id, field, contact[field])}
                                    >
                                      <Clock className="w-3 h-3 text-gray-400" />
                                      <span className="text-xs text-gray-600">
                                        {contact[field] || <span className="text-gray-400">--:--</span>}
                                      </span>
                                      <Edit3 className="w-2 h-2 text-gray-400 opacity-0 group-hover:opacity-100" />
                                    </div>
                                  )
                                ))}
                              </div>
                            </td>

                            {/* Stage */}
                            <td className="px-4 py-3">
                              <select
                                value={contact.pipelineStage}
                                onChange={(e) => handleStageChange(contact.id, e.target.value)}
                                className="text-xs px-1 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500"
                              >
                                {PIPELINE_STAGES.map(stage => (
                                  <option key={stage.id} value={stage.id}>{stage.label}</option>
                                ))}
                              </select>
                            </td>

                            {/* Actions */}
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => openEditModal(contact)}
                                  className="text-gray-400 hover:text-blue-600"
                                  title="Edit lead"
                                >
                                  <Edit3 className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleDuplicateContact(contact)}
                                  className="text-gray-400 hover:text-orange-600"
                                  title="Duplicate contact"
                                >
                                  <Copy className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => copyToNextRow(contact, sourceContacts)}
                                  className="text-gray-400 hover:text-indigo-600"
                                  title="Copy script to next row"
                                >
                                  <ArrowDown className="w-4 h-4" />
                                </button>
                                {selectedContacts.size > 0 && (
                                  <button
                                    onClick={() => copyToSelected(contact)}
                                    className="text-gray-400 hover:text-purple-600"
                                    title={`Copy script to ${selectedContacts.size} selected`}
                                  >
                                    <CopyPlus className="w-4 h-4" />
                                  </button>
                                )}
                                <button
                                  onClick={() => triggerCall(contact)}
                                  disabled={callingContactId === contact.id}
                                  className={`${callingContactId === contact.id ? 'text-green-500 animate-pulse' : 'text-gray-400 hover:text-green-600'}`}
                                  title="Call now"
                                >
                                  <PhoneCall className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleDeleteContact(contact, source)}
                                  className="text-gray-400 hover:text-red-600"
                                  title="Remove from group"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Cards View (Original) */}
      {viewMode === 'cards' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                {PIPELINE_STAGES.find(s => s.id === expandedStage)?.label} ({filteredContacts(expandedStage).length})
              </h2>
            </div>
          </div>

          <div className="divide-y divide-gray-100">
            {filteredContacts(expandedStage).length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No contacts in this stage</p>
              </div>
            ) : (
              filteredContacts(expandedStage).map((contact) => (
                <div
                  key={contact.id}
                  className="p-4 hover:bg-gray-50 cursor-pointer"
                  onClick={() => setSelectedContact(contact)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <h3 className="font-medium text-gray-900">{contact.name}</h3>
                        {contact.tags?.map((tag, idx) => (
                          <span key={idx} className="px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded-full">
                            {tag}
                          </span>
                        ))}
                      </div>
                      <div className="mt-1 flex items-center gap-4 text-sm text-gray-500">
                        {contact.company && (
                          <span className="flex items-center gap-1">
                            <Building2 className="w-3.5 h-3.5" />
                            {contact.company}
                          </span>
                        )}
                        {contact.industry && (
                          <span className="flex items-center gap-1">
                            <Tag className="w-3.5 h-3.5" />
                            {contact.industry}
                          </span>
                        )}
                        {contact.phoneNumber && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-3.5 h-3.5" />
                            {callablePhone(contact.phoneNumber)}
                          </span>
                        )}
                      </div>
                      {contact.aiIntegrationNotes && (
                        <div className="mt-2 flex items-start gap-1 text-sm text-indigo-600">
                          <Sparkles className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                          <span>{contact.aiIntegrationNotes}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {contact.nextFollowUp && (
                        <span className="flex items-center gap-1 text-xs text-orange-600 bg-orange-50 px-2 py-1 rounded">
                          <Clock className="w-3 h-3" />
                          Follow up: {new Date(contact.nextFollowUp).toLocaleDateString()}
                        </span>
                      )}
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Contact Detail Modal */}
      {selectedContact && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">{selectedContact.name}</h2>
                  {selectedContact.company && (
                    <p className="text-gray-500 flex items-center gap-1 mt-1">
                      <Building2 className="w-4 h-4" />
                      {selectedContact.company}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => setSelectedContact(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Contact Info */}
              <div className="grid grid-cols-2 gap-4">
                {selectedContact.phoneNumber && (
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="w-4 h-4 text-gray-400" />
                    <span>{selectedContact.phoneNumber}</span>
                  </div>
                )}
                {selectedContact.email && (
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="w-4 h-4 text-gray-400" />
                    <span>{selectedContact.email}</span>
                  </div>
                )}
                {selectedContact.website && (
                  <div className="flex items-center gap-2 text-sm">
                    <Globe className="w-4 h-4 text-gray-400" />
                    <a href={`https://${selectedContact.website}`} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">
                      {selectedContact.website}
                    </a>
                  </div>
                )}
                {selectedContact.address && (
                  <div className="flex items-center gap-2 text-sm col-span-2">
                    <MapPin className="w-4 h-4 text-gray-400" />
                    <span>{selectedContact.address}</span>
                  </div>
                )}
              </div>

              {/* AI Integration Notes */}
              {selectedContact.aiIntegrationNotes && (
                <div className="p-4 bg-indigo-50 rounded-lg">
                  <div className="flex items-center gap-2 text-indigo-700 font-medium mb-2">
                    <Sparkles className="w-4 h-4" />
                    AI Integration Potential
                  </div>
                  <p className="text-sm text-indigo-600">{selectedContact.aiIntegrationNotes}</p>
                </div>
              )}

              {/* Pipeline Stage */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Move to Stage</label>
                <div className="flex flex-wrap gap-2">
                  {PIPELINE_STAGES.map((stage) => (
                    <button
                      key={stage.id}
                      onClick={() => handleStageChange(selectedContact.id, stage.id)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium border-2 transition-all ${
                        selectedContact.pipelineStage === stage.id
                          ? `${stage.color} border-current`
                          : 'bg-white border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {stage.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t border-gray-200">
                <button
                  onClick={() => {
                    triggerCall(selectedContact);
                    setSelectedContact(null);
                  }}
                  disabled={callingContactId === selectedContact?.id}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  <PhoneCall className="w-4 h-4" />
                  {callingContactId === selectedContact?.id ? 'Calling...' : 'Call Now'}
                </button>
                <button className="flex-1 flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">
                  <Calendar className="w-4 h-4" />
                  Schedule Follow-up
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">Import Leads from CSV</h2>
                <button
                  onClick={() => {
                    setShowImportModal(false);
                    setImportResults(null);
                    setImportData('');
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tags (comma separated)
                </label>
                <input
                  type="text"
                  value={importTags}
                  onChange={(e) => setImportTags(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  placeholder="e.g., Heights Business Chamber, AI Candidates"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  CSV Data (paste your CSV content)
                </label>
                <textarea
                  value={importData}
                  onChange={(e) => setImportData(e.target.value)}
                  rows={10}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 font-mono text-sm"
                  placeholder="Name,Phone,Intro,Q1,Q2,Outro,Context,Date,CallTime1,CallTime2,CallTime3&#10;John Smith,555-1234,Hello John...,How are you?,Can we schedule?,Thanks!,Friend,12-25-2025,09:00,14:00,18:00"
                />
              </div>

              {importResults && (
                <div className={`p-4 rounded-lg ${importResults.error ? 'bg-red-50' : 'bg-green-50'}`}>
                  {importResults.error ? (
                    <div className="flex items-center gap-2 text-red-700">
                      <AlertCircle className="w-5 h-5" />
                      {importResults.error}
                    </div>
                  ) : (
                    <div className="space-y-1 text-sm">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        <p className="text-green-700 font-medium">Import completed!</p>
                      </div>
                      <p className="text-green-600">Created: {importResults.created}</p>
                      <p className="text-green-600">Updated: {importResults.updated}</p>
                      {importResults.skipped > 0 && (
                        <p className="text-yellow-600">Skipped: {importResults.skipped}</p>
                      )}
                      <p className="text-green-500 text-xs mt-2">Closing automatically...</p>
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4">
                <button
                  onClick={() => {
                    setShowImportModal(false);
                    setImportResults(null);
                    setImportData('');
                  }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleImport}
                  disabled={!importData.trim() || importLoading}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                >
                  {importLoading ? 'Importing...' : 'Import Leads'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* AI Configuration Modal - Per Group */}
      {showAiConfigModal && editingGroupAiConfig && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">AI Configuration</h2>
                  <p className="text-sm text-gray-500 mt-1">
                    Configure AI behavior for <span className="font-medium text-indigo-600">{editingGroupAiConfig}</span> group
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowAiConfigModal(false);
                    setEditingGroupAiConfig(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Pipeline & Voice Selection */}
              <div className="space-y-4">
                <h4 className="font-medium text-gray-900">Pipeline & Voice</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Pipeline</label>
                    <select
                      value={currentGroupConfig.pipeline || 'layer1'}
                      onChange={(e) => handleGroupPipelineChange(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    >
                      {VOICE_PIPELINE_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      {VOICE_PIPELINE_OPTIONS.find(p => p.value === (currentGroupConfig.pipeline || 'layer1'))?.description}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Voice</label>
                    <select
                      value={currentGroupConfig.voiceModel || 'coral'}
                      onChange={(e) => setCurrentGroupConfig({ ...currentGroupConfig, voiceModel: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    >
                      {(VOICE_OPTIONS_BY_PIPELINE[currentGroupConfig.pipeline || 'layer1'] || []).map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Call Provider Selection */}
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Call Provider</label>
                  <select
                    value={currentGroupConfig.callProvider || 'retell'}
                    onChange={(e) => setCurrentGroupConfig({ ...currentGroupConfig, callProvider: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="retell">Retell AI (Recommended)</option>
                    <option value="vonage">Vonage / OpenAI Realtime (Legacy)</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {currentGroupConfig.callProvider === 'retell'
                      ? 'Uses Retell AI for reliable outbound calling with automatic voicemail detection'
                      : 'Uses traditional Vonage/OpenAI Realtime API for calls'}
                  </p>
                </div>
              </div>

              {/* Use Main Config Toggle */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">Use Main AI Configuration</p>
                  <p className="text-sm text-gray-500">Use your tenant's default AI settings for this group</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={currentGroupConfig.useMainAiConfig}
                    onChange={(e) => setCurrentGroupConfig({ ...currentGroupConfig, useMainAiConfig: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                </label>
              </div>

              {/* Custom AI Config Fields */}
              {!currentGroupConfig.useMainAiConfig && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      AI Personality / Role
                    </label>
                    <textarea
                      value={currentGroupConfig.aiPersonality}
                      onChange={(e) => setCurrentGroupConfig({ ...currentGroupConfig, aiPersonality: e.target.value })}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      placeholder="e.g., You are a friendly sales representative from MuleWorks..."
                    />
                    <p className="text-xs text-gray-500 mt-1">Define who the AI should be during the call</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tone
                    </label>
                    <select
                      value={currentGroupConfig.aiTone}
                      onChange={(e) => setCurrentGroupConfig({ ...currentGroupConfig, aiTone: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="friendly">Friendly - Warm and approachable</option>
                      <option value="professional">Professional - Polished and business-like</option>
                      <option value="casual">Casual - Relaxed and conversational</option>
                      <option value="urgent">Urgent - Enthusiastic with a sense of importance</option>
                      <option value="warm">Warm - Genuinely caring and personable</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Additional Instructions
                    </label>
                    <textarea
                      value={currentGroupConfig.aiInstructions}
                      onChange={(e) => setCurrentGroupConfig({ ...currentGroupConfig, aiInstructions: e.target.value })}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      placeholder="e.g., Focus on understanding their pain points. Mention our 30-day trial offer..."
                    />
                    <p className="text-xs text-gray-500 mt-1">Specific instructions for how the AI should conduct the call</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Internal Context (Not Spoken Aloud)
                    </label>
                    <textarea
                      value={currentGroupConfig.aiContext}
                      onChange={(e) => setCurrentGroupConfig({ ...currentGroupConfig, aiContext: e.target.value })}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      placeholder="e.g., This is a cold call to small businesses. Focus on AI integration services..."
                    />
                    <p className="text-xs text-gray-500 mt-1">Background context the AI should know but NOT speak aloud</p>
                  </div>
                </div>
              )}

              {/* Save Button */}
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  onClick={() => {
                    setShowAiConfigModal(false);
                    setEditingGroupAiConfig(null);
                  }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={saveGroupAiConfig}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  Save Configuration
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Lead Modal */}
      {showAddLeadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">Add New Lead</h2>
                <button
                  onClick={() => setShowAddLeadModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Basic Info */}
              <div className="space-y-4">
                <h3 className="font-medium text-gray-900 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Basic Information
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Name / Business Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={newLeadData.name}
                      onChange={(e) => setNewLeadData({ ...newLeadData, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                      placeholder="John Smith or ABC Company"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      value={newLeadData.phoneNumber}
                      onChange={(e) => setNewLeadData({ ...newLeadData, phoneNumber: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Company
                    </label>
                    <input
                      type="text"
                      value={newLeadData.company}
                      onChange={(e) => setNewLeadData({ ...newLeadData, company: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                      placeholder="Company Name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      value={newLeadData.email}
                      onChange={(e) => setNewLeadData({ ...newLeadData, email: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                      placeholder="email@example.com"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Lead Source / Group
                    </label>
                    <select
                      value={newLeadData.leadSource}
                      onChange={(e) => setNewLeadData({ ...newLeadData, leadSource: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    >
                      <option value="Focus Group">Focus Group</option>
                      <option value="Heights Business Chamber">Heights Business Chamber</option>
                      <option value="Asian Chamber">Asian Chamber</option>
                      <option value="QA Testing">QA Testing</option>
                      <option value="manual">Manual Entry</option>
                      <option value="other">Other</option>
                      {/* Include any custom groups that exist in the data */}
                      {Object.keys(getGroupedContacts())
                        .filter(g => !['Focus Group', 'Heights Business Chamber', 'Asian Chamber', 'QA Testing', 'manual', 'other', 'Other', 'csv_import'].includes(g))
                        .map(g => (
                          <option key={g} value={g}>{g}</option>
                        ))
                      }
                      <option value="custom">+ Create New Group...</option>
                    </select>
                  </div>
                  {newLeadData.leadSource === 'custom' && (
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        New Group Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={newLeadData.customLeadSource}
                        onChange={(e) => setNewLeadData({ ...newLeadData, customLeadSource: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                        placeholder="Enter new group name (e.g., QA Tester Numbers)"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Call Script (Optional) */}
              <div className="space-y-4">
                <h3 className="font-medium text-gray-900 flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  Call Script (Optional)
                </h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Intro Message
                    </label>
                    <textarea
                      value={newLeadData.introMessage}
                      onChange={(e) => setNewLeadData({ ...newLeadData, introMessage: e.target.value })}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                      placeholder="Hello, this is Alfred calling on behalf of..."
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Question 1
                      </label>
                      <textarea
                        value={newLeadData.question1}
                        onChange={(e) => setNewLeadData({ ...newLeadData, question1: e.target.value })}
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                        placeholder="What challenges are you facing with..."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Question 2
                      </label>
                      <textarea
                        value={newLeadData.question2}
                        onChange={(e) => setNewLeadData({ ...newLeadData, question2: e.target.value })}
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                        placeholder="Would you be interested in a demo..."
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Outro / Closing Message
                    </label>
                    <textarea
                      value={newLeadData.outroMessage}
                      onChange={(e) => setNewLeadData({ ...newLeadData, outroMessage: e.target.value })}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                      placeholder="Thank you for your time..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Call Context (Internal Notes)
                    </label>
                    <textarea
                      value={newLeadData.callContext}
                      onChange={(e) => setNewLeadData({ ...newLeadData, callContext: e.target.value })}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                      placeholder="Background info about this contact..."
                    />
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  onClick={() => setShowAddLeadModal(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddLead}
                  disabled={addLeadLoading || !newLeadData.name || !newLeadData.phoneNumber}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  {addLeadLoading ? 'Adding...' : 'Add Lead'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Lead Modal */}
      {showEditModal && editingLead && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">Edit Lead</h2>
                <button
                  onClick={() => { setShowEditModal(false); setEditingLead(null); }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Basic Info */}
              <div className="space-y-4">
                <h3 className="font-medium text-gray-900 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Basic Information
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Name / Business Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={editLeadData.name}
                      onChange={(e) => setEditLeadData({ ...editLeadData, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="John Smith or ABC Company"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      value={editLeadData.phoneNumber}
                      onChange={(e) => setEditLeadData({ ...editLeadData, phoneNumber: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Company
                    </label>
                    <input
                      type="text"
                      value={editLeadData.company}
                      onChange={(e) => setEditLeadData({ ...editLeadData, company: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Company Name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      value={editLeadData.email}
                      onChange={(e) => setEditLeadData({ ...editLeadData, email: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="email@example.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Industry
                    </label>
                    <input
                      type="text"
                      value={editLeadData.industry}
                      onChange={(e) => setEditLeadData({ ...editLeadData, industry: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., Healthcare, HVAC, Restaurant"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Website
                    </label>
                    <input
                      type="text"
                      value={editLeadData.website}
                      onChange={(e) => setEditLeadData({ ...editLeadData, website: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="www.example.com"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Address
                    </label>
                    <input
                      type="text"
                      value={editLeadData.address}
                      onChange={(e) => setEditLeadData({ ...editLeadData, address: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="123 Main St, City, State"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Lead Source / Group
                    </label>
                    <select
                      value={editLeadData.leadSource}
                      onChange={(e) => setEditLeadData({ ...editLeadData, leadSource: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="Focus Group">Focus Group</option>
                      <option value="Heights Business Chamber">Heights Business Chamber</option>
                      <option value="Asian Chamber">Asian Chamber</option>
                      <option value="QA Testing">QA Testing</option>
                      <option value="manual">Manual Entry</option>
                      <option value="other">Other</option>
                      {/* Include any custom groups that exist in the data */}
                      {Object.keys(getGroupedContacts())
                        .filter(g => !['Focus Group', 'Heights Business Chamber', 'Asian Chamber', 'QA Testing', 'manual', 'other', 'Other', 'csv_import'].includes(g))
                        .map(g => (
                          <option key={g} value={g}>{g}</option>
                        ))
                      }
                    </select>
                  </div>
                </div>
              </div>

              {/* Call Script */}
              <div className="space-y-4">
                <h3 className="font-medium text-gray-900 flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  Call Script
                </h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Intro Message
                    </label>
                    <textarea
                      value={editLeadData.introMessage}
                      onChange={(e) => setEditLeadData({ ...editLeadData, introMessage: e.target.value })}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Hello, this is Alfred calling on behalf of..."
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Question 1
                      </label>
                      <textarea
                        value={editLeadData.question1}
                        onChange={(e) => setEditLeadData({ ...editLeadData, question1: e.target.value })}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="What challenges are you facing with..."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Question 2
                      </label>
                      <textarea
                        value={editLeadData.question2}
                        onChange={(e) => setEditLeadData({ ...editLeadData, question2: e.target.value })}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="Would you be interested in a demo..."
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Outro / Closing Message
                    </label>
                    <textarea
                      value={editLeadData.outroMessage}
                      onChange={(e) => setEditLeadData({ ...editLeadData, outroMessage: e.target.value })}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Thank you for your time..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Call Context (Internal Notes)
                    </label>
                    <textarea
                      value={editLeadData.callContext}
                      onChange={(e) => setEditLeadData({ ...editLeadData, callContext: e.target.value })}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Background info about this contact..."
                    />
                  </div>
                </div>
              </div>

              {/* Call Times */}
              <div className="space-y-4">
                <h3 className="font-medium text-gray-900 flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Call Times
                </h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Time 1</label>
                    <input
                      type="text"
                      value={editLeadData.callTime1}
                      onChange={(e) => setEditLeadData({ ...editLeadData, callTime1: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="HH:MM"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Time 2</label>
                    <input
                      type="text"
                      value={editLeadData.callTime2}
                      onChange={(e) => setEditLeadData({ ...editLeadData, callTime2: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="HH:MM"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Time 3</label>
                    <input
                      type="text"
                      value={editLeadData.callTime3}
                      onChange={(e) => setEditLeadData({ ...editLeadData, callTime3: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="HH:MM"
                    />
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  value={editLeadData.notes}
                  onChange={(e) => setEditLeadData({ ...editLeadData, notes: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Additional notes..."
                />
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  onClick={() => { setShowEditModal(false); setEditingLead(null); }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleEditLead}
                  disabled={editLeadLoading || !editLeadData.name || !editLeadData.phoneNumber}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {editLeadLoading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesPipelinePage;
